import { useMemo } from 'react'
import { parseSeries } from '../../components/Summary/seriesParser'
import {
    assignAxisToOption,
    parseCategoryAxis,
    parseValueAxis,
} from '../../components/Summary/axisParser'
import {
    applyAxisDefaults,
    applyColumnResize,
    applyFontScaling,
    applyLegendAndTextLimits,
    applyLegendIconSizeTransformation,
    applyLegendPositioning,
    applySeriesLabels,
    applyThemePreferences,
    cleanupPositionOverrides,
    configureTooltipsAndAxes,
    ensureAxisOverridesAlignment,
    getBaseChartOption,
    handleAxisTitles,
    handleBarChartRadius,
    handleChartSorting,
    handleNegativeValues,
    handleSpecializedCharts,
    handleStackedChartStyling,
    parseLegendData,
} from '../../components/Summary/optionUtils'
import { getDefaultChartPreferences } from '../../components/screens/Explorer/DrawerTabs/StyleTab/utils'
import { cloneDeep, merge, omit } from 'lodash'

/**
 * Builds ECharts options from summary data and view config.
 * Exported for use in both hook and direct calls.
 *
 * @param {Object} summaryData - Data returned from API
 * @param {Object} viewConfig - View configuration (chart title, series type, etc.)
 * @param {Object} theme - Theme object for styling
 * @param {Object} embeddedSizeProps - Size props for embedded rendering
 * @param {boolean} slack - Whether rendering for Slack
 * @returns {Object|null} - ECharts option object or null if no data
 */
export const buildChartOptions = (summaryData, viewConfig, theme, embeddedSizeProps, slack) => {
    if (!summaryData || !viewConfig) return null

    // Build a summaryConfig object that matches what the parsers expect
    // This bridges the new split config approach with existing parsing functions
    const summaryConfig = {
        ...viewConfig,
        // Include data-related fields if they exist in viewConfig for backwards compat
        categoryAxis: viewConfig.categoryAxis || 'Time',
    }

    // Handle different view types
    if (summaryConfig.view === 'table') {
        return summaryData
    }

    if (summaryConfig.view === 'kpi') {
        return summaryData
    }

    // Chart view - build full ECharts option
    return buildChartOption(summaryData, summaryConfig, theme, embeddedSizeProps, slack)
}

/**
 * Internal function to build chart options.
 * Follows the same pattern as useSummaryDataParser.parseChartData
 */
const buildChartOption = (summaryData, summaryConfig, theme, embeddedSizeProps, slack) => {
    // Get default theme
    const defaultTheme = JSON.parse(getDefaultChartPreferences())

    // Clone and apply theme to style overrides
    const overrides = cloneDeep(summaryConfig.styleOverrides)
    const themedOverrides = applyThemePreferences(overrides, defaultTheme, theme)

    // Theme post-processing
    handleBarChartRadius(themedOverrides, summaryConfig)
    cleanupPositionOverrides(themedOverrides)
    configureTooltipsAndAxes(themedOverrides, summaryConfig)
    applyAxisDefaults(themedOverrides)
    applyFontScaling(themedOverrides, embeddedSizeProps, summaryConfig)

    // Parse data into series
    const parsedSeries = parseSeries(summaryConfig, summaryData, themedOverrides, defaultTheme)
    const valueAxis = parseValueAxis(summaryData, summaryConfig)
    const categoryAxis = parseCategoryAxis(summaryData, summaryConfig)
    const parsedLegendData = parseLegendData(parsedSeries, summaryData)

    // Build base option
    const option = getBaseChartOption(
        parsedSeries,
        parsedLegendData,
        theme,
        summaryConfig,
        summaryData
    )

    // Assign axes
    assignAxisToOption(option, summaryConfig, summaryData, valueAxis, categoryAxis, theme)

    // Option post-processing
    ensureAxisOverridesAlignment(option, overrides, summaryConfig)
    applyColumnResize(summaryConfig, summaryData, option, themedOverrides, parsedLegendData)
    handleSpecializedCharts(option, themedOverrides, defaultTheme, summaryConfig)
    handleStackedChartStyling(option, themedOverrides, summaryConfig)
    handleNegativeValues(option, themedOverrides)
    applyLegendAndTextLimits(option, themedOverrides, summaryConfig, slack)
    applyLegendPositioning(option, themedOverrides)
    handleChartSorting(option, summaryConfig)
    handleAxisTitles(option, summaryConfig, slack)
    applySeriesLabels(option, summaryConfig)
    applyLegendIconSizeTransformation(option)

    // Final merge - omit chart-type specific overrides that were already applied
    const propsToOmit = [
        'waterfall',
        'bar',
        'line',
        'pie',
        'donut',
        'pictorialBar',
        'gauge',
        'radialBar',
        'table',
        'xAxis',
        'yAxis',
    ]
    const cleanedOverrides = omit(themedOverrides, propsToOmit)
    merge({}, option, cleanedOverrides)

    return option
}

/**
 * Hook version of buildChartOptions with memoization.
 * Use this when you need the options to be memoized based on dependencies.
 *
 * @param {Object} summaryData - Data returned from API
 * @param {Object} viewConfig - View configuration
 * @param {Object} theme - Theme object
 * @param {Object} embeddedSizeProps - Size props for embedded rendering
 * @param {boolean} slack - Whether rendering for Slack
 * @returns {Object|null} - Memoized ECharts option object
 */
export const useSummaryChartOptions = (
    summaryData,
    viewConfig,
    theme,
    embeddedSizeProps,
    slack
) => {
    return useMemo(
        () => buildChartOptions(summaryData, viewConfig, theme, embeddedSizeProps, slack),
        [summaryData, viewConfig, theme, embeddedSizeProps, slack]
    )
}
